import React, { useState } from 'react';

import classNames from 'classnames';

import isEmpty from 'lodash/isEmpty';

import Button from '@/components/Button';
import DateOfBirth from '@/components/DateOfBirth/DateOfBirth';
import Input from '@/components/Input';
import NotificationError from '@/components/NotificationError';
import { isAgeValidForSignup } from '@/helpers/dateUtil';

import { content as defaultContent } from './helper';
import styles from './SignUpSocialMedia.module.scss';

type SignUpSocialMediaProps = {
  content?: {
    firstNameHeading?: string;
    lastnameHeading?: string;
    emailHeading?: string;
    dobHeading?: string;
    signupContinue?: string;
  };
  initialFormState?: {
    firstname?: string;
    lastname?: string;
    email?: string;
    dob?: string;
  };
  formFieldErrorMessages?: {
    firstname?: string;
    lastname?: string;
    email?: string;
    dob?: string;
  };
  serverErrorMsg?: string[] | string;
  isErrorBoxVisible?: boolean;
  isContinueButtonDisabled?: boolean;
  onFieldChange?: Function;
  onSubmit?: Function;
};

const SignUpSocialMedia = ({
  content = {},
  initialFormState = {},
  formFieldErrorMessages = {},
  serverErrorMsg = [],
  isErrorBoxVisible = false,
  isContinueButtonDisabled = true,
  onFieldChange = () => {},
  onSubmit = () => {},
}: SignUpSocialMediaProps) => {
  const data = isEmpty(content) ? defaultContent : content;
  const { firstNameHeading, lastnameHeading, emailHeading, dobHeading, signupContinue } = data;
  const [disabled, setDisabled] = useState(true);
  const [isMinAgeError, setIsMinAgeError] = useState(false);
  const [formState, setFormState] = useState(initialFormState);

  const onSubmitClick = () => {
    onSubmit(formState as { firstname: string; lastname: string; email: string; dob: string });
  };

  const validate = (form: typeof formState) => {
    if (form.firstname && form.lastname && form.email && form.dob) {
      setDisabled(false);
    }
  };

  const onInputChange = (name: keyof typeof formState) => (value: string) => {
    setFormState({
      ...formState,
      [name]: value,
    });
    onFieldChange({ name, value });
    validate({
      ...formState,
      [name]: value,
    });
  };

  const handleFirstNameChange = onInputChange('firstname');
  const handleLastNameChange = onInputChange('lastname');
  const handleEmailChange = onInputChange('email');

  const handleDOBChange = ({ value }: { value: string }) => {
    const isAgeError = !isAgeValidForSignup(value);

    setIsMinAgeError(isAgeError);
    setFormState({
      ...formState,
      dob: value,
    });
    onFieldChange({
      name: 'dob',
      value,
    });
    validate({
      ...formState,
      dob: value,
    });
  };

  return (
    <>
      {isErrorBoxVisible && <NotificationError mainText="This page contains errors 🤦" subText={serverErrorMsg} icon />}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmitClick();
        }}
      >
        <div className={styles.inputWrapper}>
          <Input
            type="text"
            onChange={handleFirstNameChange}
            label={firstNameHeading}
            value={formState.firstname ?? ''}
            required
            labelContainerClass={styles.labelContainer}
            inputContainerClass={styles.inputContainer}
            inputContainerErrorClass={styles.inputContainerError}
            error={!!serverErrorMsg?.includes(formFieldErrorMessages.firstname ?? '')}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            type="text"
            onChange={handleLastNameChange}
            value={formState.lastname ?? ''}
            label={lastnameHeading}
            labelContainerClass={styles.labelContainer}
            inputContainerClass={styles.inputContainer}
            inputContainerErrorClass={styles.inputContainerError}
            required
            error={!!serverErrorMsg?.includes(formFieldErrorMessages.lastname ?? '')}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            type="email"
            onChange={handleEmailChange}
            value={formState.email ?? ''}
            label={emailHeading}
            required
            labelContainerClass={styles.labelContainer}
            inputContainerClass={styles.inputContainer}
            inputContainerErrorClass={styles.inputContainerError}
            error={!!serverErrorMsg?.includes(formFieldErrorMessages.email ?? '')}
          />
        </div>
        <div className={classNames(styles.inputWrapper, styles.dobWrapper)}>
          <label htmlFor={dobHeading}>{`${dobHeading}*`}</label>
          <DateOfBirth
            dobValue={formState.dob ?? ''}
            onChangeDob={handleDOBChange}
            className={styles.dob}
            isMinAgeError={isMinAgeError}
          />
        </div>
        <Button
          type="submit"
          className={styles.submitButton}
          disabled={disabled || isContinueButtonDisabled || isMinAgeError}
          variant="primary"
        >
          {signupContinue}
        </Button>
      </form>
    </>
  );
};

export default SignUpSocialMedia;
